import type {
  CostConstants,
  Process as CostProcess,
  Equipment,
  MaterialDensity,
} from "@/lib/cost/master";
import { DEFAULT_SCRAP_RECOVERY_RATE } from "@/lib/cost/cncQuote";
import type {
  CncLaborRate,
  CncMachineExpense,
  CncRawMaterial,
  CncSettings,
  CncSurfaceTreatment,
  Customer,
  LineItem,
  Material,
  MonthlyPoint,
  PriceHistoryPoint,
  Process,
  Quote,
  Staff,
  StatusKey,
} from "@/lib/data";
import type {
  FeedbackComment,
  FeedbackPost,
  FeedbackPriority,
  FeedbackStatus,
  FeedbackType,
  Me,
} from "@/lib/feedback";
// api.ts — typed REST client for the CMS backend.
// Wire format is snake_case; the app types are camelCase. This module owns the
// translation in both directions so the rest of the app only sees camelCase.
import { firebaseAuth } from "@/lib/firebase";
import type { QuoteAttachment } from "@/lib/quoteFiles";

const BASE = import.meta.env.VITE_API_URL || "http://localhost:8000";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

type Json = Record<string, unknown> | unknown[];

// Core request helper: injects the Firebase ID token when a user is signed in
// (the backend allows anonymous calls in dev/CI), sets JSON headers, and throws
// a typed ApiError on non-2xx.
// 슈퍼어드민 임퍼소네이션 — SUPERADMIN_KEY 로 서명된 토큰을 bearer 로 쓰면 대상
// 유저로 완전 로그인(쓰기 포함)된다. localStorage 에 저장해 새로고침(그리고
// Playwright 네비게이션)에도 유지한다.
const IMP_KEY = "jwcms_impersonation";
export interface Impersonation {
  token: string;
  uid: string;
  email: string;
  name: string;
}

export function getImpersonation(): Impersonation | null {
  try {
    const raw = localStorage.getItem(IMP_KEY);
    return raw ? (JSON.parse(raw) as Impersonation) : null;
  } catch {
    return null;
  }
}

export function setImpersonation(imp: Impersonation | null): void {
  if (imp) localStorage.setItem(IMP_KEY, JSON.stringify(imp));
  else localStorage.removeItem(IMP_KEY);
}

// 임퍼소네이션 중이면 그 토큰, 아니면 Firebase ID 토큰을 bearer 로 쓴다.
async function authToken(): Promise<string | null> {
  const imp = getImpersonation();
  if (imp) return imp.token;
  return firebaseAuth.getIdToken();
}

// 임퍼소네이션 토큰 만료(401) → 세션 정리 후 새로고침해 원래 계정으로 복귀
// (만료 토큰이 UI 를 영구히 막지 않도록). 리로드 루프는 imp 존재 여부로 방지.
function bounceIfImpersonationExpired(statusCode: number): void {
  if (statusCode === 401 && getImpersonation()) {
    setImpersonation(null);
    window.location.reload();
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await authToken();
  const headers = new Headers(init.headers);
  if (init.body != null) headers.set("Content-Type", "application/json");
  headers.set("Accept", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(`${BASE}/api${path}`, { ...init, headers });

  if (!res.ok) {
    bounceIfImpersonationExpired(res.status);
    let message = res.statusText;
    try {
      const body = await res.json();
      message = (body?.detail ?? body?.message ?? message) as string;
    } catch {
      // non-JSON error body — keep statusText
    }
    throw new ApiError(res.status, message);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export interface ImpersonationTokenResponse extends Impersonation {
  expires_in: number;
}

// 슈퍼어드민 세션으로 대상 유저의 임퍼소네이션 토큰 발급(웹 "이 사용자로 로그인").
export async function mintImpersonationToken(uid: string): Promise<ImpersonationTokenResponse> {
  return request<ImpersonationTokenResponse>("/superadmin/impersonation-token", {
    method: "POST",
    body: JSON.stringify({ uid }),
  });
}

const body = (data: Json): string => JSON.stringify(data);

// ----- Wire <-> app mappers -------------------------------------------------

interface QuoteWire {
  quote_id: string;
  project: string;
  customer_id: string;
  status: StatusKey;
  owner: string;
  owner_uid?: string;
  approver: string;
  loss_reason?: string | null;
  created: string;
  updated: string;
  sop: string;
  purpose?: string;
  items: LineItem[];
  quote_type?: "general" | "cnc";
  cnc?: import("@/lib/data").CncPart[];
  attachments?: QuoteAttachment[];
  remark?: string | null;
  opinion?: string | null;
  customer_doc?: import("@/lib/data").CustomerDoc | null;
  versions?: Quote["versions"];
  logs?: Quote["logs"];
  totals?: { internal: number; submit: number; profit: number; profit_rate: number };
}

function quoteFromWire(w: QuoteWire): Quote {
  return {
    id: w.quote_id,
    project: w.project,
    customerId: w.customer_id,
    status: w.status,
    owner: w.owner,
    ownerUid: w.owner_uid ?? "",
    approver: w.approver,
    lossReason: w.loss_reason ?? undefined,
    created: w.created,
    updated: w.updated,
    sop: w.sop,
    purpose: w.purpose ?? "",
    items: w.items ?? [],
    quoteType: w.quote_type ?? "general",
    cnc: w.cnc ?? [],
    attachments: w.attachments ?? [],
    remark: w.remark ?? undefined,
    opinion: w.opinion ?? undefined,
    customerDoc: w.customer_doc ?? undefined,
    versions: w.versions ?? [],
    logs: w.logs ?? [],
  };
}

// Payload for create — totals are recomputed server-side, so we only send the
// editable fields in snake_case.
function quoteToWire(q: Partial<Quote>): Json {
  const out: Record<string, unknown> = {};
  // Backend generates quote_id/created/updated when omitted; send quote_id only
  // when we already have one (e.g. future edit flows).
  if (q.id !== undefined) out.quote_id = q.id;
  if (q.project !== undefined) out.project = q.project;
  if (q.customerId !== undefined) out.customer_id = q.customerId;
  if (q.status !== undefined) out.status = q.status;
  if (q.owner !== undefined) out.owner = q.owner;
  if (q.approver !== undefined) out.approver = q.approver;
  if (q.lossReason !== undefined) out.loss_reason = q.lossReason;
  if (q.created !== undefined) out.created = q.created;
  if (q.updated !== undefined) out.updated = q.updated;
  if (q.sop !== undefined) out.sop = q.sop;
  if (q.purpose !== undefined) out.purpose = q.purpose;
  if (q.items !== undefined) out.items = q.items;
  if (q.quoteType !== undefined) out.quote_type = q.quoteType;
  if (q.cnc !== undefined) out.cnc = q.cnc;
  if (q.attachments !== undefined) out.attachments = q.attachments;
  if (q.remark !== undefined) out.remark = q.remark;
  if (q.opinion !== undefined) out.opinion = q.opinion;
  if (q.customerDoc !== undefined) out.customer_doc = q.customerDoc;
  if (q.versions !== undefined) out.versions = q.versions;
  if (q.logs !== undefined) out.logs = q.logs;
  return out;
}

interface MaterialWire {
  id: string;
  name: string;
  spec: string;
  unit: string;
  base_mat: number;
}
const materialFromWire = (w: MaterialWire): Material => ({
  id: w.id,
  name: w.name,
  spec: w.spec,
  unit: w.unit,
  baseMat: w.base_mat,
});
const materialToWire = (m: Partial<Material>): Json => {
  const out: Record<string, unknown> = {};
  if (m.id !== undefined) out.id = m.id;
  if (m.name !== undefined) out.name = m.name;
  if (m.spec !== undefined) out.spec = m.spec;
  if (m.unit !== undefined) out.unit = m.unit;
  if (m.baseMat !== undefined) out.base_mat = m.baseMat;
  return out;
};

// ----- CNC 마스터 wire <-> app mappers ----------------------------------------

interface CncRawMaterialWire {
  id: string;
  name: string;
  raw_unit_price: number;
  scrap_unit_price: number;
  density: number;
  g_per_mm3: number;
  recovery_rate?: number;
}
const cncRawMaterialFromWire = (w: CncRawMaterialWire): CncRawMaterial => ({
  id: w.id,
  name: w.name,
  rawUnitPrice: w.raw_unit_price,
  scrapUnitPrice: w.scrap_unit_price,
  density: w.density ?? 0,
  gPerMm3: w.g_per_mm3 ?? 0,
  recoveryRate: w.recovery_rate ?? DEFAULT_SCRAP_RECOVERY_RATE,
});
const cncRawMaterialToWire = (m: Partial<CncRawMaterial>): Json => {
  const out: Record<string, unknown> = {};
  if (m.id !== undefined) out.id = m.id;
  if (m.name !== undefined) out.name = m.name;
  if (m.rawUnitPrice !== undefined) out.raw_unit_price = m.rawUnitPrice;
  if (m.scrapUnitPrice !== undefined) out.scrap_unit_price = m.scrapUnitPrice;
  if (m.density !== undefined) out.density = m.density;
  if (m.gPerMm3 !== undefined) out.g_per_mm3 = m.gPerMm3;
  if (m.recoveryRate !== undefined) out.recovery_rate = m.recoveryRate;
  return out;
};

interface CncSurfaceTreatmentWire {
  id: string;
  name: string;
  unit_price: number;
}
const cncSurfaceTreatmentFromWire = (w: CncSurfaceTreatmentWire): CncSurfaceTreatment => ({
  id: w.id,
  name: w.name,
  unitPrice: w.unit_price,
});
const cncSurfaceTreatmentToWire = (s: Partial<CncSurfaceTreatment>): Json => {
  const out: Record<string, unknown> = {};
  if (s.id !== undefined) out.id = s.id;
  if (s.name !== undefined) out.name = s.name;
  if (s.unitPrice !== undefined) out.unit_price = s.unitPrice;
  return out;
};

interface CncLaborRateWire {
  id: string;
  process: string;
  equipment: string;
  headcount: number;
  hourly_rate: number;
}
const cncLaborRateFromWire = (w: CncLaborRateWire): CncLaborRate => ({
  id: w.id,
  process: w.process,
  equipment: w.equipment,
  headcount: w.headcount,
  hourlyRate: w.hourly_rate,
});
const cncLaborRateToWire = (r: Partial<CncLaborRate>): Json => {
  const out: Record<string, unknown> = {};
  if (r.id !== undefined) out.id = r.id;
  if (r.process !== undefined) out.process = r.process;
  if (r.equipment !== undefined) out.equipment = r.equipment;
  if (r.headcount !== undefined) out.headcount = r.headcount;
  if (r.hourlyRate !== undefined) out.hourly_rate = r.hourlyRate;
  return out;
};

interface CncMachineExpenseWire {
  id: string;
  process: string;
  equipment: string;
  equipment_price: number;
  area_m2: number;
  building_years: number;
  equipment_years: number;
  yearly_work_days: number;
  daily_work_hours: number;
  aux_equipment_ratio: number;
  building_cost: number;
  repair_ratio: number;
  power_kw: number;
  load_ratio: number;
  power_unit_price: number;
  indirect_expense_ratio: number;
}
const cncMachineExpenseFromWire = (w: CncMachineExpenseWire): CncMachineExpense => ({
  id: w.id,
  process: w.process,
  equipment: w.equipment,
  equipmentPrice: w.equipment_price,
  areaM2: w.area_m2,
  buildingYears: w.building_years,
  equipmentYears: w.equipment_years,
  yearlyWorkDays: w.yearly_work_days,
  dailyWorkHours: w.daily_work_hours,
  auxEquipmentRatio: w.aux_equipment_ratio,
  buildingCost: w.building_cost,
  repairRatio: w.repair_ratio,
  powerKw: w.power_kw,
  loadRatio: w.load_ratio,
  powerUnitPrice: w.power_unit_price,
  indirectExpenseRatio: w.indirect_expense_ratio,
});
const cncMachineExpenseToWire = (e: Partial<CncMachineExpense>): Json => {
  const out: Record<string, unknown> = {};
  if (e.id !== undefined) out.id = e.id;
  if (e.process !== undefined) out.process = e.process;
  if (e.equipment !== undefined) out.equipment = e.equipment;
  if (e.equipmentPrice !== undefined) out.equipment_price = e.equipmentPrice;
  if (e.areaM2 !== undefined) out.area_m2 = e.areaM2;
  if (e.buildingYears !== undefined) out.building_years = e.buildingYears;
  if (e.equipmentYears !== undefined) out.equipment_years = e.equipmentYears;
  if (e.yearlyWorkDays !== undefined) out.yearly_work_days = e.yearlyWorkDays;
  if (e.dailyWorkHours !== undefined) out.daily_work_hours = e.dailyWorkHours;
  if (e.auxEquipmentRatio !== undefined) out.aux_equipment_ratio = e.auxEquipmentRatio;
  if (e.buildingCost !== undefined) out.building_cost = e.buildingCost;
  if (e.repairRatio !== undefined) out.repair_ratio = e.repairRatio;
  if (e.powerKw !== undefined) out.power_kw = e.powerKw;
  if (e.loadRatio !== undefined) out.load_ratio = e.loadRatio;
  if (e.powerUnitPrice !== undefined) out.power_unit_price = e.powerUnitPrice;
  if (e.indirectExpenseRatio !== undefined) out.indirect_expense_ratio = e.indirectExpenseRatio;
  return out;
};

interface CncSettingsWire {
  allowance: number;
  general_admin_rate: number;
  material_admin_rate: number;
  indirect_expense_ratio: number;
}
const cncSettingsFromWire = (w: CncSettingsWire): CncSettings => ({
  allowance: w.allowance,
  generalAdminRate: w.general_admin_rate,
  materialAdminRate: w.material_admin_rate,
  indirectExpenseRatio: w.indirect_expense_ratio,
});
const cncSettingsToWire = (s: Partial<CncSettings>): Json => {
  const out: Record<string, unknown> = {};
  if (s.allowance !== undefined) out.allowance = s.allowance;
  if (s.generalAdminRate !== undefined) out.general_admin_rate = s.generalAdminRate;
  if (s.materialAdminRate !== undefined) out.material_admin_rate = s.materialAdminRate;
  if (s.indirectExpenseRatio !== undefined) out.indirect_expense_ratio = s.indirectExpenseRatio;
  return out;
};

// ----- Quotes ---------------------------------------------------------------
export const getQuotes = async (): Promise<Quote[]> =>
  (await request<QuoteWire[]>("/quotes")).map(quoteFromWire);

export const getQuote = async (id: string): Promise<Quote> =>
  quoteFromWire(await request<QuoteWire>(`/quotes/${encodeURIComponent(id)}`));

export const createQuote = async (q: Partial<Quote>): Promise<Quote> =>
  quoteFromWire(
    await request<QuoteWire>("/quotes", { method: "POST", body: body(quoteToWire(q)) })
  );

export const updateQuote = async (id: string, q: Partial<Quote>): Promise<Quote> =>
  quoteFromWire(
    await request<QuoteWire>(`/quotes/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: body(quoteToWire(q)),
    })
  );

export const deleteQuote = (id: string): Promise<void> =>
  request<void>(`/quotes/${encodeURIComponent(id)}`, { method: "DELETE" });

export const updateQuoteStatus = async (
  id: string,
  status: StatusKey,
  lossReason?: string
): Promise<Quote> =>
  quoteFromWire(
    await request<QuoteWire>(`/quotes/${encodeURIComponent(id)}/status`, {
      method: "PATCH",
      body: body({ status, loss_reason: lossReason ?? null }),
    })
  );

// ----- Customers ------------------------------------------------------------
export const getCustomers = (): Promise<Customer[]> => request<Customer[]>("/customers");
export const createCustomer = (c: Omit<Customer, "id"> & { id?: string }): Promise<Customer> =>
  request<Customer>("/customers", { method: "POST", body: body(c) });
export const updateCustomer = (id: string, patch: Partial<Customer>): Promise<Customer> =>
  request<Customer>(`/customers/${encodeURIComponent(id)}`, { method: "PATCH", body: body(patch) });
export const deleteCustomer = (id: string): Promise<void> =>
  request<void>(`/customers/${encodeURIComponent(id)}`, { method: "DELETE" });

// ----- Materials ------------------------------------------------------------
export const getMaterials = async (): Promise<Material[]> =>
  (await request<MaterialWire[]>("/materials")).map(materialFromWire);
export const createMaterial = async (
  m: Omit<Material, "id"> & { id?: string }
): Promise<Material> =>
  materialFromWire(
    await request<MaterialWire>("/materials", { method: "POST", body: body(materialToWire(m)) })
  );
export const updateMaterial = async (id: string, patch: Partial<Material>): Promise<Material> =>
  materialFromWire(
    await request<MaterialWire>(`/materials/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: body(materialToWire(patch)),
    })
  );
export const deleteMaterial = (id: string): Promise<void> =>
  request<void>(`/materials/${encodeURIComponent(id)}`, { method: "DELETE" });

// ----- Processes ------------------------------------------------------------
export const getProcesses = (): Promise<Process[]> => request<Process[]>("/processes");
export const createProcess = (p: Omit<Process, "id"> & { id?: string }): Promise<Process> =>
  request<Process>("/processes", { method: "POST", body: body(p) });
export const updateProcess = (id: string, patch: Partial<Process>): Promise<Process> =>
  request<Process>(`/processes/${encodeURIComponent(id)}`, { method: "PATCH", body: body(patch) });
export const deleteProcess = (id: string): Promise<void> =>
  request<void>(`/processes/${encodeURIComponent(id)}`, { method: "DELETE" });

// ----- Staff ----------------------------------------------------------------
export const getStaff = (): Promise<Staff[]> => request<Staff[]>("/staff");
export const createStaff = (s: Omit<Staff, "id"> & { id?: string }): Promise<Staff> =>
  request<Staff>("/staff", { method: "POST", body: body(s) });
export const updateStaff = (id: string, patch: Partial<Staff>): Promise<Staff> =>
  request<Staff>(`/staff/${encodeURIComponent(id)}`, { method: "PATCH", body: body(patch) });
export const deleteStaff = (id: string): Promise<void> =>
  request<void>(`/staff/${encodeURIComponent(id)}`, { method: "DELETE" });

// ----- CNC 마스터 -------------------------------------------------------------
export const getCncRawMaterials = async (): Promise<CncRawMaterial[]> =>
  (await request<CncRawMaterialWire[]>("/cnc/raw-materials")).map(cncRawMaterialFromWire);
export const createCncRawMaterial = async (
  m: Omit<CncRawMaterial, "id"> & { id?: string }
): Promise<CncRawMaterial> =>
  cncRawMaterialFromWire(
    await request<CncRawMaterialWire>("/cnc/raw-materials", {
      method: "POST",
      body: body(cncRawMaterialToWire(m)),
    })
  );
export const updateCncRawMaterial = async (
  id: string,
  patch: Partial<CncRawMaterial>
): Promise<CncRawMaterial> =>
  cncRawMaterialFromWire(
    await request<CncRawMaterialWire>(`/cnc/raw-materials/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: body(cncRawMaterialToWire(patch)),
    })
  );
export const deleteCncRawMaterial = (id: string): Promise<void> =>
  request<void>(`/cnc/raw-materials/${encodeURIComponent(id)}`, { method: "DELETE" });

export const getCncSurfaceTreatments = async (): Promise<CncSurfaceTreatment[]> =>
  (await request<CncSurfaceTreatmentWire[]>("/cnc/surface-treatments")).map(
    cncSurfaceTreatmentFromWire
  );
export const createCncSurfaceTreatment = async (
  s: Omit<CncSurfaceTreatment, "id"> & { id?: string }
): Promise<CncSurfaceTreatment> =>
  cncSurfaceTreatmentFromWire(
    await request<CncSurfaceTreatmentWire>("/cnc/surface-treatments", {
      method: "POST",
      body: body(cncSurfaceTreatmentToWire(s)),
    })
  );
export const updateCncSurfaceTreatment = async (
  id: string,
  patch: Partial<CncSurfaceTreatment>
): Promise<CncSurfaceTreatment> =>
  cncSurfaceTreatmentFromWire(
    await request<CncSurfaceTreatmentWire>(`/cnc/surface-treatments/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: body(cncSurfaceTreatmentToWire(patch)),
    })
  );
export const deleteCncSurfaceTreatment = (id: string): Promise<void> =>
  request<void>(`/cnc/surface-treatments/${encodeURIComponent(id)}`, { method: "DELETE" });

export const getCncLaborRates = async (): Promise<CncLaborRate[]> =>
  (await request<CncLaborRateWire[]>("/cnc/labor-rates")).map(cncLaborRateFromWire);
export const createCncLaborRate = async (
  r: Omit<CncLaborRate, "id"> & { id?: string }
): Promise<CncLaborRate> =>
  cncLaborRateFromWire(
    await request<CncLaborRateWire>("/cnc/labor-rates", {
      method: "POST",
      body: body(cncLaborRateToWire(r)),
    })
  );
export const updateCncLaborRate = async (
  id: string,
  patch: Partial<CncLaborRate>
): Promise<CncLaborRate> =>
  cncLaborRateFromWire(
    await request<CncLaborRateWire>(`/cnc/labor-rates/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: body(cncLaborRateToWire(patch)),
    })
  );
export const deleteCncLaborRate = (id: string): Promise<void> =>
  request<void>(`/cnc/labor-rates/${encodeURIComponent(id)}`, { method: "DELETE" });

export const getCncMachineExpenses = async (): Promise<CncMachineExpense[]> =>
  (await request<CncMachineExpenseWire[]>("/cnc/machine-expenses")).map(cncMachineExpenseFromWire);
export const createCncMachineExpense = async (
  e: Omit<CncMachineExpense, "id"> & { id?: string }
): Promise<CncMachineExpense> =>
  cncMachineExpenseFromWire(
    await request<CncMachineExpenseWire>("/cnc/machine-expenses", {
      method: "POST",
      body: body(cncMachineExpenseToWire(e)),
    })
  );
export const updateCncMachineExpense = async (
  id: string,
  patch: Partial<CncMachineExpense>
): Promise<CncMachineExpense> =>
  cncMachineExpenseFromWire(
    await request<CncMachineExpenseWire>(`/cnc/machine-expenses/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: body(cncMachineExpenseToWire(patch)),
    })
  );
export const deleteCncMachineExpense = (id: string): Promise<void> =>
  request<void>(`/cnc/machine-expenses/${encodeURIComponent(id)}`, { method: "DELETE" });

export const getCncSettings = async (): Promise<CncSettings> =>
  cncSettingsFromWire(await request<CncSettingsWire>("/cnc/settings"));
export const updateCncSettings = async (patch: Partial<CncSettings>): Promise<CncSettings> =>
  cncSettingsFromWire(
    await request<CncSettingsWire>("/cnc/settings", {
      method: "PATCH",
      body: body(cncSettingsToWire(patch)),
    })
  );

// ----- Misc reads -----------------------------------------------------------
export const getLossReasons = (): Promise<string[]> => request<string[]>("/loss-reasons");
export const getMonthly = (): Promise<MonthlyPoint[]> =>
  request<MonthlyPoint[]>("/reports/monthly");
export const getPriceHistory = (item: string): Promise<PriceHistoryPoint[]> =>
  request<PriceHistoryPoint[]>(`/reports/price-history?item=${encodeURIComponent(item)}`);

// ----- Cost master (read-only reference tables) -----------------------------
// The same tables also live statically in `@/lib/cost/master`; this fetch lets a
// UI render the server's copy. Wire is snake_case → mapped to the lib's camelCase.
interface EquipmentWire {
  name: string;
  price: number | null;
  power_kw: number | null;
  area_m2: number | null;
  headcount: number;
}
interface RawMaterialWire {
  id: string;
  name: string;
  raw_unit_price: number;
  scrap_unit_price: number;
  density: number;
  g_per_mm3: number;
  recovery_rate?: number;
}
interface SurfaceTreatmentWire {
  id: string;
  name: string;
  unit_price: number;
}
interface QuoteSettingsWire {
  allowance: number;
  general_admin_rate: number;
  material_admin_rate: number;
}
interface CostMasterWire {
  processes: {
    key: string;
    equipment: EquipmentWire;
    power_load_ratio: number | null;
    labor_rate?: number | null; // 임율(₩/HR) — 공정 행으로 통합 (구 임율 섹션)
    display_order?: number | null; // 분해표 공정 컬럼 노출순서(작을수록 앞). 서버가 backfill
  }[];
  // deprecated — 서버가 공정 행에서 재파생. 읽기 fallback 용으로만 유지.
  labor_rates: { process: string; rate: number }[];
  densities: { series: string; density: number; g_per_mm3: number }[];
  raw_materials: RawMaterialWire[];
  surface_treatments: SurfaceTreatmentWire[];
  quote_settings: QuoteSettingsWire | null;
  constants: {
    power_unit_price: number;
    aux_equipment_ratio: number;
    building_cost: number;
    building_years: number;
    equipment_years_dedicated: number;
    equipment_years_general: number;
    repair_ratio: number;
    daily_work_hours: number;
    yearly_work_days: number;
    indirect_expense_ratio: number;
  };
}

export interface CostRawMaterialRow {
  id: string;
  name: string;
  rawUnitPrice: number;
  scrapUnitPrice: number;
  density: number;
  gPerMm3: number;
  recoveryRate: number; // 스크랩 회수율 (%) — 재질별, 기본 90
}
export interface CostSurfaceTreatmentRow {
  id: string;
  name: string;
  unitPrice: number;
}
export interface QuoteSettings {
  allowance: number;
  generalAdminRate: number;
  materialAdminRate: number;
}

// 편집용 공정 행 — 정적 reference(Process)에 임율·노출순서 컬럼 추가.
export interface CostMasterProcess extends CostProcess {
  laborRate: number;
  // 견적상세 분해표의 공정 컬럼 노출순서(작을수록 앞). 0 = 미지정(서버 응답 전 fallback).
  displayOrder: number;
}

export interface CostMaster {
  processes: CostMasterProcess[];
  densities: MaterialDensity[];
  constants: CostConstants;
  rawMaterials: CostRawMaterialRow[];
  surfaceTreatments: CostSurfaceTreatmentRow[];
  quoteSettings: QuoteSettings;
}

const equipmentFromWire = (e: EquipmentWire): Equipment => ({
  name: e.name,
  price: e.price,
  powerKw: e.power_kw,
  areaM2: e.area_m2,
  headcount: e.headcount,
});

const equipmentToWire = (e: Equipment): EquipmentWire => ({
  name: e.name,
  price: e.price,
  power_kw: e.powerKw,
  area_m2: e.areaM2,
  headcount: e.headcount,
});

function costMasterFromWire(w: CostMasterWire): CostMaster {
  return {
    processes: w.processes.map((p) => ({
      key: p.key,
      equipment: equipmentFromWire(p.equipment),
      powerLoadRatio: p.power_load_ratio,
      // 임율 — 공정 행 통합값. 구 응답이면 deprecated labor_rates 에서 fallback.
      laborRate:
        p.labor_rate ?? w.labor_rates.find((r) => r.process === p.key)?.rate ?? 0,
      // 구 응답(노출순서 없음)은 0 — 분해표는 통합 전 고정 규칙으로 fallback 한다.
      displayOrder: p.display_order ?? 0,
    })),
    rawMaterials: (w.raw_materials ?? []).map((r) => ({
      id: r.id,
      name: r.name,
      rawUnitPrice: r.raw_unit_price,
      scrapUnitPrice: r.scrap_unit_price,
      density: r.density,
      gPerMm3: r.g_per_mm3,
      recoveryRate: r.recovery_rate ?? DEFAULT_SCRAP_RECOVERY_RATE,
    })),
    surfaceTreatments: (w.surface_treatments ?? []).map((t) => ({
      id: t.id,
      name: t.name,
      unitPrice: t.unit_price,
    })),
    quoteSettings: {
      allowance: w.quote_settings?.allowance ?? 0,
      generalAdminRate: w.quote_settings?.general_admin_rate ?? 0,
      materialAdminRate: w.quote_settings?.material_admin_rate ?? 0,
    },
    densities: w.densities.map((d) => ({
      series: d.series,
      density: d.density,
      gPerMm3: d.g_per_mm3,
    })),
    constants: {
      powerUnitPrice: w.constants.power_unit_price,
      auxEquipmentRatio: w.constants.aux_equipment_ratio,
      buildingCost: w.constants.building_cost,
      buildingYears: w.constants.building_years,
      equipmentYearsDedicated: w.constants.equipment_years_dedicated,
      equipmentYearsGeneral: w.constants.equipment_years_general,
      repairRatio: w.constants.repair_ratio,
      dailyWorkHours: w.constants.daily_work_hours,
      yearlyWorkDays: w.constants.yearly_work_days,
      indirectExpenseRatio: w.constants.indirect_expense_ratio,
    },
  };
}

export const getCostMaster = async (): Promise<CostMaster> =>
  costMasterFromWire(await request<CostMasterWire>("/cost/master"));

export const updateCostMaster = async (m: CostMaster): Promise<CostMaster> =>
  costMasterFromWire(
    await request<CostMasterWire>("/cost/master", {
      method: "PUT",
      body: body({
        processes: m.processes.map((p) => ({
          key: p.key,
          equipment: equipmentToWire(p.equipment),
          power_load_ratio: p.powerLoadRatio,
          labor_rate: p.laborRate,
          display_order: p.displayOrder,
        })),
        raw_materials: m.rawMaterials.map((r) => ({
          id: r.id,
          name: r.name,
          raw_unit_price: r.rawUnitPrice,
          scrap_unit_price: r.scrapUnitPrice,
          density: r.density,
          g_per_mm3: r.gPerMm3,
          recovery_rate: r.recoveryRate,
        })),
        surface_treatments: m.surfaceTreatments.map((t) => ({
          id: t.id,
          name: t.name,
          unit_price: t.unitPrice,
        })),
        quote_settings: {
          allowance: m.quoteSettings.allowance,
          general_admin_rate: m.quoteSettings.generalAdminRate,
          material_admin_rate: m.quoteSettings.materialAdminRate,
        },
        densities: m.densities.map((d) => ({
          series: d.series,
          density: d.density,
          g_per_mm3: d.gPerMm3,
        })),
        constants: {
          power_unit_price: m.constants.powerUnitPrice,
          aux_equipment_ratio: m.constants.auxEquipmentRatio,
          building_cost: m.constants.buildingCost,
          building_years: m.constants.buildingYears,
          equipment_years_dedicated: m.constants.equipmentYearsDedicated,
          equipment_years_general: m.constants.equipmentYearsGeneral,
          repair_ratio: m.constants.repairRatio,
          daily_work_hours: m.constants.dailyWorkHours,
          yearly_work_days: m.constants.yearlyWorkDays,
          indirect_expense_ratio: m.constants.indirectExpenseRatio,
        },
      }),
    })
  );

// ----- 회사 직인 ---------------------------------------------------------------
// 대표이사 직인 — 이미지 바이트는 오브젝트 스토리지(문서엔 키만, "" = 미설정).
// 업로드는 multipart PUT, 표시용 이미지는 인증 fetch 후 blob URL 로 변환.

export const uploadCompanySeal = async (file: File): Promise<string> => {
  const form = new FormData();
  form.append("file", file);
  const res = await fileRequest("/company/seal", { method: "PUT", body: form });
  return ((await res.json()) as { key: string }).key;
};

// 미설정(404) 이면 "" — 호출부는 빈 문자열을 "직인 없음" 으로 취급.
export const fetchCompanySealUrl = async (): Promise<string> => {
  try {
    const res = await fileRequest("/company/seal/image");
    return URL.createObjectURL(await res.blob());
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return "";
    throw e;
  }
};

// ----- Feedback 보드 ----------------------------------------------------------

interface FeedbackPostWire {
  id: string;
  type: FeedbackType;
  title: string;
  body: string;
  status: FeedbackStatus;
  priority: FeedbackPriority;
  images: string[];
  author_uid: string;
  author_name: string;
  created_at: string;
  updated_at: string;
}

function feedbackPostFromWire(w: FeedbackPostWire): FeedbackPost {
  return {
    id: w.id,
    type: w.type,
    title: w.title,
    body: w.body,
    status: w.status,
    priority: w.priority,
    images: w.images ?? [],
    authorUid: w.author_uid,
    authorName: w.author_name,
    createdAt: w.created_at,
    updatedAt: w.updated_at,
  };
}

interface FeedbackCommentWire {
  id: string;
  post_id: string;
  body: string;
  author_uid: string;
  author_name: string;
  created_at: string;
}

function feedbackCommentFromWire(w: FeedbackCommentWire): FeedbackComment {
  return {
    id: w.id,
    postId: w.post_id,
    body: w.body,
    authorUid: w.author_uid,
    authorName: w.author_name,
    createdAt: w.created_at,
  };
}

interface MeWire {
  uid: string;
  email: string;
  name: string;
  role: "admin" | "member";
  position: string;
  dept: string;
  images_enabled: boolean;
  is_superadmin: boolean;
}

function meFromWire(w: MeWire): Me {
  return {
    uid: w.uid,
    email: w.email,
    name: w.name,
    role: w.role,
    position: w.position,
    dept: w.dept,
    imagesEnabled: w.images_enabled,
    isSuperadmin: w.is_superadmin,
  };
}

export async function getMe(): Promise<Me> {
  return meFromWire(await request<MeWire>("/me"));
}

export async function updateMe(patch: { position?: string; dept?: string }): Promise<Me> {
  return meFromWire(await request<MeWire>("/me", { method: "PATCH", body: JSON.stringify(patch) }));
}

export async function getFeedbackPosts(filters?: {
  type?: FeedbackType;
  priority?: FeedbackPriority;
}): Promise<FeedbackPost[]> {
  const params = new URLSearchParams();
  if (filters?.type) params.set("type", filters.type);
  if (filters?.priority) params.set("priority", filters.priority);
  const qs = params.toString();
  const list = await request<FeedbackPostWire[]>(`/feedback${qs ? `?${qs}` : ""}`);
  return list.map(feedbackPostFromWire);
}

export async function createFeedbackPost(payload: {
  type: FeedbackType;
  title: string;
  body: string;
  priority: FeedbackPriority;
  images: string[];
}): Promise<FeedbackPost> {
  const w = await request<FeedbackPostWire>("/feedback", { method: "POST", body: body(payload) });
  return feedbackPostFromWire(w);
}

export async function updateFeedbackPost(
  id: string,
  patch: Partial<{
    type: FeedbackType;
    title: string;
    body: string;
    priority: FeedbackPriority;
    images: string[];
  }>
): Promise<FeedbackPost> {
  const w = await request<FeedbackPostWire>(`/feedback/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: body(patch),
  });
  return feedbackPostFromWire(w);
}

export async function deleteFeedbackPost(id: string): Promise<void> {
  await request<void>(`/feedback/${encodeURIComponent(id)}`, { method: "DELETE" });
}

export async function updateFeedbackStatus(
  id: string,
  status: FeedbackStatus
): Promise<FeedbackPost> {
  const w = await request<FeedbackPostWire>(`/feedback/${encodeURIComponent(id)}/status`, {
    method: "PATCH",
    body: body({ status }),
  });
  return feedbackPostFromWire(w);
}

export async function getFeedbackComments(postId: string): Promise<FeedbackComment[]> {
  const list = await request<FeedbackCommentWire[]>(
    `/feedback/${encodeURIComponent(postId)}/comments`
  );
  return list.map(feedbackCommentFromWire);
}

export async function createFeedbackComment(
  postId: string,
  text: string
): Promise<FeedbackComment> {
  const w = await request<FeedbackCommentWire>(`/feedback/${encodeURIComponent(postId)}/comments`, {
    method: "POST",
    body: body({ body: text }),
  });
  return feedbackCommentFromWire(w);
}

export async function deleteFeedbackComment(postId: string, commentId: string): Promise<void> {
  await request<void>(
    `/feedback/${encodeURIComponent(postId)}/comments/${encodeURIComponent(commentId)}`,
    { method: "DELETE" }
  );
}

// ----- 파일 업로드/조회 (feedback 이미지 + 견적 도면·첨부) ---------------------

async function fileRequest(path: string, init: RequestInit = {}): Promise<Response> {
  const token = await authToken();
  const headers = new Headers(init.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const res = await fetch(`${BASE}/api${path}`, { ...init, headers });
  if (!res.ok) {
    bounceIfImpersonationExpired(res.status);
    let message = res.statusText;
    try {
      const errBody = (await res.json()) as { detail?: string };
      message = errBody?.detail ?? message;
    } catch {
      // non-JSON error body
    }
    throw new ApiError(res.status, message);
  }
  return res;
}

// 업로드는 multipart 라 공용 request 헬퍼(JSON 전용)를 쓰지 않는다 — FormData 는
// 브라우저가 boundary 포함 Content-Type 을 직접 설정해야 한다.
async function uploadFile<T>(path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append("file", file);
  const res = await fileRequest(path, { method: "POST", body: form });
  return (await res.json()) as T;
}

// 파일은 인증 헤더가 필요해 <img src>/<a href> 직접 사용 불가 — blob URL 로 변환한다.
// 호출부가 URL.revokeObjectURL 로 해제할 책임을 진다.
// key 형식: <prefix>/<uuid>/<filename> — 백엔드 라우트가 {key:path} 이므로
// / 자체는 보존하고 각 세그먼트만 인코딩한다.
async function fetchFileUrl(basePath: string, key: string): Promise<string> {
  const encodedKey = key.split("/").map(encodeURIComponent).join("/");
  const res = await fileRequest(`${basePath}/${encodedKey}`);
  return URL.createObjectURL(await res.blob());
}

export async function uploadFeedbackImage(file: File): Promise<string> {
  return (await uploadFile<{ key: string }>("/feedback/images", file)).key;
}

export const fetchFeedbackImageUrl = (key: string): Promise<string> =>
  fetchFileUrl("/feedback/images", key);

export const uploadQuoteFile = (file: File): Promise<QuoteAttachment> =>
  uploadFile<QuoteAttachment>("/quotes/files", file);

export const fetchQuoteFileUrl = (key: string): Promise<string> =>
  fetchFileUrl("/quotes/files", key);

// 견적 백업(export) — 참조 고객사·마스터·이미지까지 담은 zip 을 blob 으로 받는다.
export async function exportQuoteBundle(quoteId: string): Promise<Blob> {
  const res = await fileRequest(`/quotes/${encodeURIComponent(quoteId)}/export`);
  return res.blob();
}

// 여러 견적을 하나의 멀티 번들 zip 으로 내보낸다.
export async function exportQuotesBundle(quoteIds: string[]): Promise<Blob> {
  const res = await fileRequest("/quotes/export", {
    method: "POST",
    body: JSON.stringify({ quote_ids: quoteIds }),
    headers: { "Content-Type": "application/json" },
  });
  return res.blob();
}

// 견적 가져오기 미리보기 — 새로 추가될 참조 데이터 요약(dry-run, 서버 쓰기 없음). 단일/멀티 자동 감지.
export interface ImportPlan {
  quoteCount: number;
  quotes: { sourceQuoteId: string; project: string }[];
  newCustomers: string[];
  newMaterials: string[];
  newSurfaces: string[];
  newLaborRates: string[];
  fileCount: number;
  storageRequired: boolean;
}

interface ImportPlanWire {
  quote_count: number;
  quotes: { source_quote_id: string; project: string }[];
  new_customers: string[];
  new_materials: string[];
  new_surfaces: string[];
  new_labor_rates: string[];
  file_count: number;
  storage_required: boolean;
}

export async function previewImportBundle(file: File): Promise<ImportPlan> {
  const w = await uploadFile<ImportPlanWire>("/quotes/import/preview", file);
  return {
    quoteCount: w.quote_count,
    quotes: w.quotes.map((x) => ({ sourceQuoteId: x.source_quote_id, project: x.project })),
    newCustomers: w.new_customers,
    newMaterials: w.new_materials,
    newSurfaces: w.new_surfaces,
    newLaborRates: w.new_labor_rates,
    fileCount: w.file_count,
    storageRequired: w.storage_required,
  };
}

// 견적 가져오기(import) — 단일/멀티 zip 을 현재 계정으로 이관(생성된 견적 목록 반환).
export async function importQuoteBundle(file: File): Promise<Quote[]> {
  return (await uploadFile<QuoteWire[]>("/quotes/import", file)).map(quoteFromWire);
}

// ----- Superadmin ------------------------------------------------------------
// 운영 콘솔 전용 raw 조회 — wire(snake_case) 그대로 노출한다(원본 데이터 뷰어).

export interface SuperadminUser {
  uid: string;
  email: string;
  name: string;
  role: "admin" | "member";
  position: string;
  dept: string;
  counts: Record<string, number>;
}

export interface SuperadminCollectionPage {
  total: number;
  items: Record<string, unknown>[];
}

export async function getSuperadminUsers(): Promise<SuperadminUser[]> {
  return request<SuperadminUser[]>("/superadmin/users");
}

export async function getSuperadminCollection(
  uid: string,
  name: string,
  skip: number,
  limit: number
): Promise<SuperadminCollectionPage> {
  return request<SuperadminCollectionPage>(
    `/superadmin/users/${encodeURIComponent(uid)}/collections/${encodeURIComponent(name)}?skip=${skip}&limit=${limit}`
  );
}
